import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  Grid,
  Button,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Receipt as ReceiptIcon,
  Download as DownloadIcon,
  AccountBalance as AccountBalanceIcon,
  Code as CodeIcon,
} from '@mui/icons-material';
import { useQuery } from '@apollo/client';
import { VERIFY_DOCUMENT } from '../graphql/queries';

const documentTypeLabels = {
  INVOICE: 'Фактура',
  CREDIT_NOTE: 'Кредитно известие',
  DEBIT_NOTE: 'Дебитно известие',
  PROFORMA: 'Проформа',
};

const ValidateDocument = () => {
  const { uuid } = useParams();
  const navigate = useNavigate();

  // Handler за сваляне на банков сертификат
  const handleDownloadCertificate = (base64Data) => {
    try {
      const byteCharacters = window.atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new window.Blob([byteArray], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = 'bank-certificate.pdf';
      window.document.body.appendChild(a);
      a.click();
      window.document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading certificate:', err);
      window.alert('Грешка при сваляне на сертификата');
    }
  };

  // Handler за сваляне на UBL XML
  const handleDownloadUblXml = (xmlContent, documentNumber) => {
    try {
      const blob = new window.Blob([xmlContent], { type: 'application/xml' });
      const url = window.URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = `ubl-${documentNumber}.xml`;
      window.document.body.appendChild(a);
      a.click();
      window.document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading UBL XML:', err);
      window.alert('Грешка при сваляне на UBL XML');
    }
  };

  const { loading, data, error } = useQuery(VERIFY_DOCUMENT, {
    variables: { uuid },
    skip: !uuid,
    fetchPolicy: 'network-only',
  });

  const document = data?.verifyDocument;

  // Redirect to manual verify page if no UUID provided
  useEffect(() => {
    if (!uuid) {
      navigate('/verify');
    }
  }, [uuid, navigate]);

  if (!uuid) {
    return null;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
        p: 3,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 600,
          width: '100%',
          textAlign: 'center',
        }}
      >
        <ReceiptIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />

        <Typography variant="h4" gutterBottom>
          Валидация на документ
        </Typography>

        {loading && (
          <Box sx={{ mt: 4 }}>
            <CircularProgress size={60} />
            <Typography variant="body1" sx={{ mt: 2 }}>
              Проверка на документа...
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 3 }}>
            Грешка при проверката: {error.message}
          </Alert>
        )}

        {!loading && !error && !document && (
          <Alert
            severity="error"
            icon={<CancelIcon />}
            sx={{ mt: 3 }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              Документът не е намерен
            </Typography>
            <Typography variant="body2">
              Не съществува документ с този идентификатор.
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', mt: 1, fontFamily: 'monospace' }}>
              UUID: {uuid}
            </Typography>
          </Alert>
        )}

        {document && (
          <Card sx={{ mt: 3, textAlign: 'left' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CheckCircleIcon sx={{ color: 'success.main', fontSize: 40, mr: 2 }} />
                <Box>
                  <Typography variant="h6" color="success.main">
                    Документът е валиден
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Този документ е автентичен и е издаден от нашата система.
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Тип документ</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {documentTypeLabels[document.documentType] || document.documentType}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Номер</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {document.documentNumber}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Дата на издаване</Typography>
                  <Typography variant="body1">
                    {new Date(document.issueDate).toLocaleDateString('bg-BG')}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Сума</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {document.totalAmountWithVat.toFixed(2)} {document.currencyCode}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Издател</Typography>
                  <Typography variant="body1">{document.companyName}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    ЕИК: {document.companyEik}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Получател</Typography>
                  <Typography variant="body1">{document.clientName}</Typography>
                </Grid>
              </Grid>

              {/* Банков сертификат */}
              {document.hasBankCertificate && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <AccountBalanceIcon sx={{ color: 'primary.main', mr: 1 }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Банково удостоверение
                        </Typography>
                        <Typography variant="body2">
                          {document.bankCertificateBase64 ? 'Налично за сваляне' : 'Грешка при зареждане'}
                        </Typography>
                      </Box>
                    </Box>
                    {document.bankCertificateBase64 ? (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={() => handleDownloadCertificate(document.bankCertificateBase64)}
                      >
                        Свали
                      </Button>
                    ) : (
                      <Typography variant="caption" color="error">
                        Временно недостъпно
                      </Typography>
                    )}
                  </Box>
                </>
              )}

              {/* UBL XML */}
              {document.ublXml && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CodeIcon sx={{ color: 'primary.main', mr: 1 }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          UBL XML (EN 16931 / Peppol)
                        </Typography>
                        <Typography variant="body2">
                          Електронен формат за ERP системи
                        </Typography>
                      </Box>
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleDownloadUblXml(document.ublXml, document.documentNumber)}
                    >
                      Свали
                    </Button>
                  </Box>
                </>
              )}

              <Divider sx={{ my: 2 }} />

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Уникален идентификатор
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                {uuid}
              </Typography>
            </CardContent>
          </Card>
        )}

        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button
            variant="outlined"
            href="/verify"
          >
            Ръчна проверка
          </Button>
          <Button
            variant="text"
            href="/"
          >
            Към началната страница
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default ValidateDocument;
